import React from "react";
import cx from "./PrivacyPolicy.module.scss";
import st from "../../../assets/stylesheet/style.module.scss";
import { Container, Row, Col } from "react-bootstrap";
import { Banner1 } from "../../../assets/images";
import { usePageMeta } from "../../../hooks/usePageMeta";

const PrivacyPolicy = () => {
  usePageMeta(
    "Privacy Policy - TaxiMo",
    "How TaxiMo collects, uses, and protects your personal information."
  );

  return (
    <>
      <section className={`${cx.bookingSection}`}>
        <div className={`${cx.bannersection}`}>
          <img src={Banner1} alt="Privacy Policy" />
          <div className={`${cx.bannerContent}`}>
            <h2>Privacy Policy</h2>
          </div>
        </div>
      </section>

      <section className={`${st.sectionPadding}`}>
        <Container>
          <Row>
            <Col lg={12} className="m-auto">
              <div className={`${cx.loginForm}`}>
                <div className={`${cx.contentBody}`}>
                  <h3>Privacy Policy</h3>
                  <p><strong>Last Updated: January 2024</strong></p>

                  <h4>1. Information We Collect</h4>
                  <p>TaxiMo ("we," "our," or "us") collects information to provide and improve our taxi booking services. The types of information we collect include:</p>
                  <ul>
                    <li><strong>Personal Information:</strong> Name, email address, phone number, and profile picture when you create an account.</li>
                    <li><strong>Location Data:</strong> Real-time GPS location and address information to facilitate ride pickup and drop-off. Location data is collected only when the app is in use.</li>
                    <li><strong>Payment Information:</strong> Credit card details and billing address processed securely through Stripe. We do not store full credit card numbers on our servers.</li>
                    <li><strong>Usage Data:</strong> Ride history, trip details (pickup/drop-off locations, timestamps, routes), app interaction data, and device information including IP address, browser type, and operating system.</li>
                    <li><strong>Communications:</strong> Messages exchanged with drivers, customer support inquiries, and feedback provided.</li>
                  </ul>

                  <h4>2. How We Use Your Information</h4>
                  <p>We use the collected information for the following purposes:</p>
                  <ul>
                    <li>To create and manage your account</li>
                    <li>To process ride bookings, connect you with drivers, and facilitate real-time ride tracking</li>
                    <li>To process payments and generate receipts</li>
                    <li>To provide customer support and respond to inquiries</li>
                    <li>To send transactional communications including booking confirmations, driver updates, and receipts</li>
                    <li>To improve our services, develop new features, and conduct analytics</li>
                    <li>To detect and prevent fraud, abuse, and security incidents</li>
                    <li>To comply with legal obligations and enforce our Terms and Conditions</li>
                  </ul>

                  <h4>3. Information Sharing</h4>
                  <p>We share your information only in the following circumstances:</p>
                  <ul>
                    <li><strong>With Drivers:</strong> Your name, pickup/drop-off locations, and contact number are shared with your assigned driver to facilitate the ride.</li>
                    <li><strong>With Payment Processors:</strong> Payment card information is securely transmitted to Stripe for payment processing. Stripe's privacy policy governs their use of your data.</li>
                    <li><strong>Service Providers:</strong> We engage third-party services for cloud hosting (Amazon Web Services), mapping (Google Maps), data storage and push notifications (Google Firebase), and bot verification (Cloudflare Turnstile). These providers are contractually bound to protect your data.</li>
                    <li><strong>Legal Compliance:</strong> We may disclose information if required by law, court order, or governmental regulation, or to protect the rights, property, or safety of TaxiMo, our users, or the public.</li>
                    <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of the transaction, subject to the same privacy protections.</li>
                  </ul>

                  <h4>4. Data Retention</h4>
                  <p>We retain your personal information for as long as your account is active or as needed to provide you services. Ride history records are retained for a minimum of 7 years to comply with Canadian tax and business record-keeping requirements. When you delete your account, your personal information is removed within 30 days, except where retention is required by law.</p>

                  <h4>5. Data Security</h4>
                  <p>We implement industry-standard security measures to protect your information, including:</p>
                  <ul>
                    <li>Encryption of data in transit (TLS 1.2+) and at rest (AES-256)</li>
                    <li>Secure access controls and authentication mechanisms</li>
                    <li>Regular security assessments and vulnerability scanning</li>
                    <li>Employee training on data protection and privacy practices</li>
                  </ul>
                  <p>However, no method of electronic storage or transmission is 100% secure. We cannot guarantee absolute security of your data.</p>

                  <h4>6. Your Rights</h4>
                  <p>Under Canadian privacy laws (PIPEDA) and applicable provincial legislation, you have the right to:</p>
                  <ul>
                    <li>Access the personal information we hold about you</li>
                    <li>Request correction of inaccurate or incomplete information</li>
                    <li>Withdraw consent for the collection, use, or disclosure of your personal information (subject to legal or contractual restrictions)</li>
                    <li>Request deletion of your account and associated data</li>
                    <li>File a complaint with the Office of the Privacy Commissioner of Canada if you believe your privacy rights have been violated</li>
                  </ul>
                  <p>To exercise these rights, please contact us at the information provided below.</p>

                  <h4>7. Cookies and Tracking Technologies</h4>
                  <p>We use cookies and similar tracking technologies to maintain your session, remember your preferences, and analyze app usage. You can control cookie settings through your browser. Disabling cookies may affect certain features of our service.</p>

                  <h4>8. Third-Party Services</h4>
                  <p>Our application integrates with the following third-party services, each of which has its own privacy policy:</p>
                  <ul>
                    <li>Amazon Web Services (site hosting, and processing waitlist signups and emails)</li>
                    <li>Google Maps API (location services and mapping)</li>
                    <li>Stripe (payment processing)</li>
                    <li>Firebase (data storage, push notifications, and analytics)</li>
                    <li>Cloudflare Turnstile (bot verification on signup forms)</li>
                  </ul>
                  <p>We encourage you to review the privacy policies of these third-party providers.</p>

                  <h4>9. Cross-Border Data Transfers</h4>
                  <p>Your information may be stored and processed in Canada, the United States, or other countries where our service providers operate. We ensure that appropriate safeguards are in place for cross-border transfers in compliance with Canadian privacy law.</p>

                  <h4>10. Children's Privacy</h4>
                  <p>Our services are not directed to individuals under the age of 13. We do not knowingly collect personal information from children under 13. If we learn we have collected such information, we will delete it promptly.</p>

                  <h4>11. Changes to This Policy</h4>
                  <p>We may update this Privacy Policy from time to time. We will notify you of material changes by email or through the app at least 30 days before they take effect. Your continued use of TaxiMo after changes take effect constitutes acceptance of the updated policy.</p>

                  <h4>12. Contact Us</h4>
                  <p>If you have questions or concerns about this Privacy Policy or our privacy practices, please contact us:</p>
                  <ul>
                    <li>Email: privacy@taximo.ca</li>
                    <li>Mail: TaxiMo Privacy Office, Toronto, ON, Canada</li>
                  </ul>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default PrivacyPolicy;
